import ActionProvider from '../store/schema/ActionProvider';
import * as Adaptive from 'adaptivecards';
import CustomAdaptiveCard from '../controls/CustomAdaptiveCard';

const ARTICLE_ACTIVITY_IMAGE_TYPE = 'article';

function parsePicture(
    json: any,
    defaultSize: Adaptive.Size = Adaptive.Size.Medium,
    defaultStyle: Adaptive.ImageStyle = Adaptive.ImageStyle.Default
): Adaptive.Image {
    const picture = new Adaptive.Image();
    picture.url = json.image;
    picture.size = json.size ? json.size : defaultSize;

    return picture;
}

function parseImageSet(json: any): Adaptive.ImageSet {
    const imageSet = new Adaptive.ImageSet();
    const imageArray = json as Array<any>;

    for (let i = 0; i < imageArray.length; i++) {
        const image = parsePicture(imageArray[i], Adaptive.Size.Large);

        imageSet.addImage(image);
    }

    return imageSet;
}

function parseFactSet(json: any): Adaptive.FactSet {
    const factSet = new Adaptive.FactSet();
    const factArray = json as Array<any>;

    for (let i = 0; i < factArray.length; i++) {
        const fact = new Adaptive.Fact();
        fact.name = factArray[i].name;
        fact.value = factArray[i].value;

        factSet.facts.push(fact);
    }

    return factSet;
}

function parseSection(json: any, actionProviders: ActionProvider[]): Adaptive.Container {
    const section = new Adaptive.Container();

    if (json.title) {
        const textBlock = new Adaptive.TextBlock();
        textBlock.text = json.title;
        textBlock.size = Adaptive.TextSize.Medium;
        textBlock.wrap = true;
        section.addItem(textBlock);
    }

    if (json.startGroup) {
        section.separator = true;
        section.spacing = Adaptive.Spacing.Large;
    } else {
        section.spacing = Adaptive.Spacing.Medium;
    }

    if (json.activityTitle || json.activitySubtitle || json.activityText || json.activityImage) {
        const columnSet = new Adaptive.ColumnSet();
        let column: Adaptive.Column;

        // Image column
        if (json.activityImage) {
            column = new Adaptive.Column();

            const image = new Adaptive.Image();
            image.size = json.activityImageSize ? json.activityImageSize : Adaptive.Size.Small;

            if (json.activityImageType && json.activityImageType === ARTICLE_ACTIVITY_IMAGE_TYPE) {
                image.style = Adaptive.ImageStyle.Default;
                image.size = Adaptive.Size.Medium;
                column.width = new Adaptive.SizeAndUnit(80, Adaptive.SizeUnit.Pixel);
            } else {
                image.style = json.activityImageStyle
                    ? json.activityImageStyle
                    : Adaptive.ImageStyle.Person;
            }

            image.url = json.activityImage;
            column.addItem(image);
            columnSet.addColumn(column);
        }

        // Text column
        column = new Adaptive.Column();

        if (json.activityTitle) {
            const textBlock = new Adaptive.TextBlock();
            textBlock.text = json.activityTitle;
            textBlock.wrap = true;
            textBlock.spacing = Adaptive.Spacing.None;
            column.addItem(textBlock);
        }

        if (json.activitySubtitle) {
            const textBlock = new Adaptive.TextBlock();
            textBlock.text = json.activitySubtitle;
            textBlock.weight = Adaptive.TextWeight.Lighter;
            textBlock.isSubtle = true;
            textBlock.wrap = true;
            textBlock.spacing = Adaptive.Spacing.None;
            column.addItem(textBlock);
        }

        if (json.activityText) {
            const textBlock = new Adaptive.TextBlock();
            textBlock.text = json.activityText;
            textBlock.wrap = true;
            textBlock.spacing = Adaptive.Spacing.None;
            column.addItem(textBlock);
        }

        columnSet.addColumn(column);
        section.addItem(columnSet);
    }

    const heroImage = json.heroImage;

    if (heroImage) {
        const image = parsePicture(heroImage);
        image.size = Adaptive.Size.Auto;
        section.addItem(image);
    }

    if (json.text) {
        const text = new Adaptive.TextBlock();
        text.text = json.text;
        text.wrap = true;
        section.addItem(text);
    }

    if (json.facts) {
        const factGroup = parseFactSet(json.facts);
        section.addItem(factGroup);
    }

    if (json.images) {
        const pictureGallery = parseImageSet(json.images);
        section.addItem(pictureGallery);
    }

    if (json.potentialAction) {
        const actionSet = parseActionSet(json.potentialAction, actionProviders);
        section.addItem(actionSet);
    }

    return section;
}

export function parseActionSet(json: any, actionProviders: ActionProvider[]): Adaptive.ActionSet {
    const actionSet = new Adaptive.ActionSet();
    actionSet.spacing = Adaptive.Spacing.Medium;
    const actionArray = json as Array<any>;

    for (let i = 0; i < actionArray.length; i++) {
        const jsonAction = actionArray[i];
        actionProviders.forEach(actionProvider => {
            if (actionProvider.parseLegacyMessageCardAction) {
                const actionFromProvider = actionProvider.parseLegacyMessageCardAction(
                    jsonAction,
                    actionProviders
                );
                if (actionFromProvider) {
                    actionSet.addAction(actionFromProvider);
                }
            }
        });
    }

    return actionSet;
}

export default function parseMessageCard(
    json: any,
    actionProviders: ActionProvider[]
): CustomAdaptiveCard {
    const adaptiveCard = new CustomAdaptiveCard();
    if (json.title) {
        const textBlock = new Adaptive.TextBlock();
        textBlock.text = json.title;
        textBlock.size = Adaptive.TextSize.Large;
        textBlock.wrap = true;
        adaptiveCard.addItem(textBlock);
    }

    if (json.text) {
        const textBlock = new Adaptive.TextBlock();
        (textBlock.text = json.text), (textBlock.wrap = true);
        adaptiveCard.addItem(textBlock);
    }

    if (json.sections) {
        const sectionArray = json.sections as Array<any>;

        for (let i = 0; i < sectionArray.length; i++) {
            const section = parseSection(sectionArray[i], actionProviders);
            adaptiveCard.addItem(section);
        }
    }

    if (json.potentialAction) {
        const actionSet = parseActionSet(json.potentialAction, actionProviders);
        adaptiveCard.addItem(actionSet);
    }

    return adaptiveCard;
}
